import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { OktaAuth, TokenParams } from '@okta/okta-auth-js';
import { BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Location } from '@angular/common';
import { UserService } from '../user.service';
import { User } from '../../models/user.model';
import { AppService } from '../app.service';
import { UserActivityService } from '../user-activity.service';
import { CookieService } from '../cookie.service';

@Injectable()
export class OktaAuthenticationProvider {
  private router = inject(Router);
  private httpClient = inject(HttpClient);
  private userService = inject(UserService);
  private userActivityService = inject(UserActivityService);
  private location = inject(Location);
  private cookieService = inject(CookieService);

  readonly oktaAuth: OktaAuth;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  protected get accessToken(): string {
    return this.oktaAuth?.getAccessToken();
  }

  constructor() {
    try {
      this.oktaAuth = new OktaAuth({
        clientId: AppService.get('oktaClientId'),
        issuer: AppService.get('oktaIssuer'),
        redirectUri: AppService.get('oktaRedirectUri'),
        pkce: true,
        responseType: 'code',
        scopes: ['offline_access', 'openid', 'email', 'profile'],
        tokenManager: {
          autoRenew: true,
          expireEarlySeconds: 120,
          storageKey: 'lc-token-storage',
          storage: 'localStorage',
          syncStorage: false,
        },
        maxClockSkew: 60 * 60 * 24,
        cookies: { secure: window.location.protocol === 'https:' },
      });
      this.oktaAuth.start();
    } catch (e) {
      console.log(e);
    }
  }

  public isAuthenticated(): boolean {
    return this.accessToken && this.isAuthenticatedSubject.value;
  }

  public async isAuthenticated$(): Promise<boolean> {
    if (this.isAuthenticated()) {
      // Already retrieved login info. Just return
      return true;
    }

    const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
    if (accessToken) {
      // If we already have an access token, try login with this. Otherwise,  return false
      let isAuthenticated = false;
      try {
        isAuthenticated = await this.handleAuthentication()
          .then(() => true)
          .catch(() => false);
      } catch {
        // Token is no longer valid, return false
        return false;
      }
      return isAuthenticated;
    }
    return false;
  }

  public redirectToLogin() {
    const brand = this.getBrand();
    if (brand) {
      this.router.navigate(['/okta/login'], { queryParams: { brand } });
    } else {
      this.router.navigate(['/okta/login']);
    }
  }
  private getBrand() {
    const search = this.location.path().split('?')[1];
    const params = new URLSearchParams(search);
    console.info(`Query parameters ${params}`);
    const brand = params.get('brand') ?? this.cookieService.getCookie('brand');
    return brand?.toLowerCase();
  }

  public login() {
    const config: TokenParams = {};

    const brand = this.getBrand();
    const cbBrands = ['cb', 'cbr', 'cba'];
    const altBrands = ['bhg', 'era', 'c21'];

    if (!AppService.isPhotoApp) {
      let idp: string;
      if (cbBrands.includes(brand)) {
        idp = AppService.get('brandedLoginPageIDP');
      } else if (altBrands.includes(brand)) {
        idp = AppService.getByString(`${brand}BrandedLoginPageIDP`);
      }
      if (idp) config.idp = idp;
    }

    return this.oktaAuth.token.getWithRedirect(config);
  }

  public async logout() {
    this.setBrandCookie();
    this.isAuthenticatedSubject.next(false);
    this.userService.clearUser('currentUser');
    this.userActivityService.stopWatching();
    await this.oktaAuth?.stop();
    await Promise.allSettled([this.oktaAuth?.revokeRefreshToken(), this.oktaAuth?.revokeAccessToken()]);
    this.oktaAuth?.tokenManager.clear();
    localStorage.removeItem('canny-sso');
    // Locally, also end the Okta SSO session so the next login doesn't silently re-authenticate off the shared tenant.
    if (AppService.get('environment') === 'local') {
      await this.oktaAuth?.signOut();
    }
    await this.router.navigate(['/logout']);
  }

  public getToken(): string {
    return this.accessToken;
  }

  /**
   * Tries to retrieve the token from the tokenManager, otherwise retrieves it from the url parameters.
   * Updates the tokenExpiration date and returns the new accessToken
   */
  private async updateAccessToken() {
    if (this.oktaAuth.isLoginRedirect()) {
      const { tokens } = await this.oktaAuth.token.parseFromUrl();
      this.oktaAuth.tokenManager.setTokens(tokens);
    }
    const accessToken = await this.oktaAuth.getOrRenewAccessToken();
    if (!accessToken) throw new Error('Sign-in has expired');
    return accessToken;
  }

  public async handleAuthentication(isPostLogin?: boolean) {
    const accessToken = await this.updateAccessToken();
    const user = isPostLogin === true
      ? await this.getUserAfterLogin(accessToken)
      : await this.getCurrentUser(accessToken);

    if (user) {
      // Renew token failing because of third party cookies
      // To move to the production we are reverting the changes
      try {
        // // Successful authentication
        // this.accessToken = tokenRenewResponse.accessToken;
        await this.userService.onAuthenticated(user);
        this.initializeTokenListeners();
        this.isAuthenticatedSubject.next(true);
      } catch (e) {
        console.error(`Error renewing access token after user update:${JSON.stringify(e)}`);
        throw e;
      }
    }
    // this.sessionExpiryWarning();
    // We have a user, start monitoring activity
    this.userActivityService.startWatching();
  }

  /**
   * If the user is already logged in, get the current-user from persist.
   * This will not update profile information or create any login audits
   */
  private async getCurrentUser(accessToken: string): Promise<User> {
    // Get the user information from LC server
    const route = `${AppService.get('persistBaseURL')}user/current-user/`;
    const headers = { Authorization: `Bearer ${accessToken}` };
    return await this.httpClient
      .get(route, { headers })
      .pipe(
        map((user) => new User(user)),
        catchError((error) => {
          console.error(`Error getting current user using auth token: ${JSON.stringify(error)}`);
          throw error;
        }),
      )
      .toPromise();
  }

  /**
   * Retrieves the user information after logging into the system.
   * This method will also update profile information and audit fields
   * on the backend.
   */
  private async getUserAfterLogin(accessToken: string): Promise<User> {
    const route = `${AppService.get('persistBaseURL')}user/login`;
    const headers = { Authorization: `Bearer ${accessToken}` };
    return await this.httpClient
      .get(route, { headers })
      .pipe(
        map((user) => new User(user)),
        catchError((error) => {
          console.error(`Error getting current user using auth token: ${JSON.stringify(error)}`);
          throw error;
        }),
      )
      .toPromise();
  }

  /**
   * Registers listeners for okta events.
   * This is necessary for automatic token refreshes handled by okta.
   * Error and expired events shouldn't happen, but register them so we can log if they do happen.
   */
  private initializeTokenListeners() {
    this.oktaAuth.tokenManager.on('error', this.tokenError);
  }

  private tokenError(err) {
    console.warn(`Token error reported by tokenManage:${JSON.stringify(err)}`);
  }

  setBrandCookie() {
    const currentUser = this.userService.getCurrentUser();
    const brand = currentUser?.profile?.office?.company?.brandCode;
    if (!brand) return;

    this.cookieService.setCookie('brand', brand, { path: '/', expires: 0 });
  }
}
